import { InboxOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Col, Form, Input, message, Row } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import clsx from 'clsx';
import { debounce, includes, map } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../AppContext';
import {
  ArchiveOutline,
  DeleteButton,
  EditIcon,
  SaveOutline,
} from '../../../../../../../assets/svg';
import {
  ALLOWED_ACTION_KEYS,
  BREAKPOINTS,
  CONFIRMATION_TYPES,
  DATE_ISO_FORMAT,
  DEFAULTDATEFORMAT,
  PROJECT_ROLES,
  PROPERTY_TYPES,
  REGISTER_STATUS_LABEL,
  ROUTES,
  SCROLL_PERCENT,
  TAB_KEYS,
  USER_ROLES,
} from '../../../../../../../common/constants';
import useRouter from '../../../../../../../common/useRouter';
import {
  formatNumericInput,
  formValidatorRules,
  toCamelCase,
} from '../../../../../../../common/utils';
import CanPerform from '../../../../../../../components/CanPerform';
import CommonCard from '../../../../../../../components/CommonCard';
import RouterPrompt from '../../../../../../../components/RouterPrompt';
import {
  UPDATE_REGISTER,
  UPDATE_REGISTER_LOG_DETAIL_ENTRY_LIST,
} from '../../../../../graphql/Mutation';
import {
  GET_REGISTER,
  GET_REGISTER_LOG_DETAILS_ENTRY,
  REGISTER_LIST,
} from '../../../../../graphql/Queries';
import AddEditRegisterFieldsTable from '../AddEditRegisterFieldsTable';
import ConfirmationModal from '../ConfirmationModal';
import DeleteRegisterModal from '../DeleteRegisterModal';
import RenderFields from '../RenderFields';
import RegisterDetailsTable from './RegisterDetailsTable';
import SaveRegisterLogModal from './SaveRegisterLogModal';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const RegisterPropertyDetails = ({ registerDetailsData }) => {
  const { state, getCurrentUserRole, dispatch } = useContext(AppContext);
  const [form] = Form.useForm();
  const {
    location,
    navigate,
    params: { projectId, registerId, registerLogId },
  } = useRouter();
  const initialFilter = {
    limit: 10,
    skip: 0,
  };
  const registerName = useWatch('registerName', form);
  const [registerData, setRegisterData] = useState();
  const [isEditableProperty, setIsEditableProperty] = useState(false);
  const properties = [];
  const [propertyDetails, setPropertyDetails] = useState([]);
  const [totalDetailsCount, setTotalDetailsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditControl, setIsEditControl] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteRegisterModal, setShowDeleteRegisterModal] = useState(false);
  const [confirmationType, setConfirmationType] = useState(
    CONFIRMATION_TYPES?.EDIT,
  );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isOptionsFocused, setIsOptionsFocused] = useState(null);
  const [isMultiSelectOptionsFocused, setIsMultiSelectOptionsFocused] =
    useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [isDateFocused, setIsDateFocused] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const showDetailPrompt = useRef(false);
  const showTableFieldsPrompt = useRef(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const isUserAssignedToProject = !!state?.projectRole;
  const isAuditor =
    getCurrentUserRole() === USER_ROLES.EXTERNAL_AUDITOR ||
    state?.projectRole === PROJECT_ROLES.AUDITOR;

  const showRouterPrompt = (value) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: value,
    });
  };

  const [registerList] = useLazyQuery(REGISTER_LIST, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted() {},
  });

  const [getRegisterLogDetails, { loading }] = useLazyQuery(
    GET_REGISTER_LOG_DETAILS_ENTRY,
    {
      fetchPolicy: 'network-only',
      onCompleted(res) {
        const data = res?.registerLogDetailEntryList?.data;
        setTotalDetailsCount(res?.registerLogDetailEntryList?.total);
        const processValue = (item, value) => {
          const isDefaultDateFormat =
            /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(value);
          if (item?.type === PROPERTY_TYPES.DATE && value) {
            return moment(value, DEFAULTDATEFORMAT, true).isValid()
              ? moment(
                  value,
                  isDefaultDateFormat ? DEFAULTDATEFORMAT : DATE_ISO_FORMAT,
                )
              : '';
          }
          return value || null;
        };

        const result = data?.map((item) => {
          const value = item?.registerLogDetailEntries?.value;
          return {
            name: item?.name,
            index: toCamelCase(item?.name),
            id: item?.registerLogDetailEntries?.id,
            value: processValue(item, value),
            registerDetailId: item?.id,
            type: item?.type,
            options: item?.options,
            isRequired: item?.isRequired,
            updatedAt: item?.registerLogDetailEntries?.updatedAt,
            createdAt: item?.registerLogDetailEntries?.createdAt,
          };
        });
        if (scrollFlag) {
          const dataList = [...propertyDetails, ...result];
          setPropertyDetails(dataList);
          setScrollFlag(false);
        } else {
          const dataList = [...result];
          setPropertyDetails(dataList);
        }
        setHasMore(!!data?.length);
        setIsLoading(false);
      },
      onError() {},
    },
  );

  const [getRegister] = useLazyQuery(GET_REGISTER, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted(response) {
      if (response?.getRegister?.status === REGISTER_STATUS_LABEL.DRAFT) {
        setIsEditable(true);
      }
      setRegisterData(response?.getRegister);
      form.setFieldValue('registerName', response?.getRegister?.name);
      const registerFormFields = response?.getRegister?.registerFormFields;
      const registerDetails = response?.getRegister?.registerDetails;
      setPropertyDetails(registerDetails);
      setTableColumns(registerFormFields);
    },
  });

  const [updateRegister] = useMutation(UPDATE_REGISTER, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted() {
      registerList({ variables: { projectId, filter: {} } });
    },
  });

  const [updateRegisterLogDetail] = useMutation(
    UPDATE_REGISTER_LOG_DETAIL_ENTRY_LIST,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted() {
        showDetailPrompt.current = false;
        showRouterPrompt(
          showDetailPrompt?.current || showTableFieldsPrompt?.current,
        );
      },
    },
  );

  const transformDataToColumns = (data) => {
    if (!data || !data.length) return [];
    const sampleObject = data[0];

    const columns = Object.keys(sampleObject)
      .filter((key) => !['registerLogId', 'createdAt'].includes(key))
      .map((key) => ({
        title: key,
        dataIndex: key,
        key,
        index: key,
      }));

    return columns;
  };

  useEffect(() => {
    if (registerDetailsData?.length) {
      const columns = transformDataToColumns(registerDetailsData);
      setTableColumns(columns);
    }
  }, [registerDetailsData]);

  useEffect(() => {
    if (
      location?.pathname !==
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
    ) {
      if (
        includes(
          [REGISTER_STATUS_LABEL.ARCHIVED, REGISTER_STATUS_LABEL.DRAFT],
          registerDetailsData?.status,
        )
      ) {
        setIsDisabled(true);
      }
      setIsLoading(true);
      getRegisterLogDetails({
        variables: { registerLogId, filter: initialFilter },
      });
    }
  }, []);

  useEffect(() => {
    if (
      location?.pathname ===
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
    ) {
      getRegister({ variables: { getRegisterId: registerId } });
      setIsEditControl(true);
    }
  }, []);

  useEffect(() => {
    const updatedFields = map(
      registerDetailsData?.registerFormFields,
      (field) => ({
        ...field,
        name: field?.name?.toUpperCase(),
        dataIndex: toCamelCase(field?.name),
        index: toCamelCase(field?.name),
      }),
    );
    setTableColumns(updatedFields);
  }, [registerDetailsData]);

  const handleRegisterRefetch = () => {
    getRegister({ variables: { getRegisterId: registerId } });
  };

  const handleRegisterLogDetailRefetch = async () => {
    await getRegisterLogDetails({
      variables: { registerLogId, filter: initialFilter },
    });
  };

  const handleRegisterArchive = () => {
    setConfirmationType(CONFIRMATION_TYPES.ARCHIVE);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (
      propertyDetails?.length === 1 &&
      propertyDetails[0]?.isRequired === false
    ) {
      message.destroy();
      message.error('There must be at-least 1 required detail field!');
      return;
    }
    setConfirmationType(CONFIRMATION_TYPES.SAVE);
    setShowModal(true);
  };

  const handleDetailBlur = async (field) => {
    try {
      await form?.validateFields([field]);

      const initialValues = propertyDetails?.reduce((acc, item) => {
        acc[item?.index] = {
          id: item?.registerDetailId,
          registerDetailId: item?.id,
          value: item?.value,
        };
        return acc;
      }, {});
      const detailValues = form?.getFieldsValue();
      const updatedValue = detailValues?.[field];
      const fieldConfig = propertyDetails?.find(
        (item) => item?.index === field,
      );

      if (initialValues?.[field]?.id) {
        let processedValue = updatedValue;

        if (fieldConfig?.type === PROPERTY_TYPES.DATE && updatedValue) {
          if (moment.isMoment(updatedValue)) {
            processedValue = updatedValue?.format(DEFAULTDATEFORMAT);
          } else {
            processedValue = updatedValue;
          }
        }
        if (
          fieldConfig?.type === PROPERTY_TYPES.CHECKBOXES &&
          Array.isArray(updatedValue)
        ) {
          processedValue = updatedValue?.join(',');
        }

        processedValue =
          processedValue === undefined || processedValue === null
            ? ''
            : processedValue;

        const result = {
          registerDetailId: initialValues?.[field]?.id,
          id: initialValues?.[field]?.registerDetailId,
          value: processedValue,
        };

        if (
          (fieldConfig?.isRequired && processedValue !== '') ||
          !fieldConfig?.isRequired
        ) {
          await updateRegisterLogDetail({
            variables: { registerLogId, entries: [result] },
          });
        }
      }
    } catch (error) {
      showDetailPrompt.current = true;
      showRouterPrompt(true);
      return error;
    }
  };
  const debouncedHandleDetailBlur = debounce(handleDetailBlur, 500);

  useEffect(() => {
    return () => {
      debouncedHandleDetailBlur.cancel();
    };
  }, []);

  const handleTableRouterPromptUpdate = (value) => {
    showTableFieldsPrompt.current = value;
  };

  const renderProperties = (property) => {
    const result = property.reduce((acc, item) => {
      let { value } = item;
      if (
        item?.type === PROPERTY_TYPES.CHECKBOXES &&
        typeof value === 'string' &&
        value
      ) {
        value = value.split(',');
      }
      acc[item.index] = value || undefined;
      return acc;
    }, {});

    return map(property, (detail) => {
      const options = detail?.options?.map((option) => ({
        label: option,
        value: option,
      }));

      if (!isEditControl) {
        return (
          <RenderFields
            fieldType={detail?.type}
            fieldIndex={detail?.index}
            fieldName={detail?.name}
            fieldIsRequired={detail?.isRequired}
            initialFieldValue={result[detail?.index]}
            fieldDisabled={isAuditor || isDisabled || !isUserAssignedToProject}
            onTextChange={(e) => {
              const { value } = e?.target;
              form.setFieldsValue({ [detail.index]: value || undefined });
              debouncedHandleDetailBlur(detail?.index);
            }}
            onNumericChange={(e) => {
              const { value } = e?.target;
              const formattedValue = formatNumericInput(value);
              form.setFieldsValue({
                [detail.index]: formattedValue,
              });
              debouncedHandleDetailBlur(detail?.index);
            }}
            optionsOpen={isOptionsFocused === detail?.index}
            onOptionsFocus={() => setIsOptionsFocused(detail?.index)}
            onOptionChange={(value) => {
              const newValue = value?.length > 0 ? value : undefined;
              form.setFieldsValue({ [detail.index]: newValue });
              handleDetailBlur(detail?.index);
            }}
            onOptionDropdownVisibilityChange={(e) => {
              if (!e) {
                setIsOptionsFocused();
              } else {
                setIsOptionsFocused(detail?.index);
              }
            }}
            onOptionOpenChange={() => {
              setIsOptionsFocused(null);
            }}
            options={options}
            onOptionBlur={() => setIsOptionsFocused(null)}
            getPopupContainer={() =>
              // eslint-disable-next-line no-undef
              document?.querySelector('.ant-card-body')
            }
            multiSelectOpen={isMultiSelectOptionsFocused === detail?.index}
            onMultiSelectFocus={() =>
              setIsMultiSelectOptionsFocused(detail?.index)
            }
            onMultiSelectBlur={() => setIsMultiSelectOptionsFocused(null)}
            onMultiSelectDropdownVisibilityChange={(e) => {
              if (!e) {
                setIsMultiSelectOptionsFocused();
              } else {
                setIsMultiSelectOptionsFocused(detail?.index);
              }
            }}
            onMultiSelectChange={(value) => {
              const newValue = value?.length > 0 ? value : undefined;
              form.setFieldsValue({ [detail.index]: newValue });
              handleDetailBlur(detail?.index);
            }}
            dateOpen={isDateFocused === detail?.index}
            onDateFocused={() => setIsDateFocused(detail?.index)}
            onDateOpenChange={(e) => {
              if (!e) {
                setIsDateFocused();
                handleDetailBlur(detail?.index);
              }
            }}
            onDateChange={async (date) => {
              try {
                form.setFieldsValue({ [detail.index]: date || undefined });
                await form.validateFields([detail.index]);
              } catch (error) {
                showDetailPrompt.current = true;
                showRouterPrompt(true);
                return error;
              }
            }}
          />
        );
      }
    });
  };

  const renderPropertiesDetails = () => {
    return (
      <RegisterDetailsTable
        isEditable={isEditable}
        propertyDetails={propertyDetails}
        setPropertyDetails={setPropertyDetails}
        setTableColumns={setTableColumns}
        isAuditor={isAuditor}
        handleRegisterRefetch={handleRegisterRefetch}
      />
    );
  };

  const handleScroll = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      (scrollTop + offsetHeight) / scrollHeight >= SCROLL_PERCENT;
    if (scrolledToBottom && hasMore && !loading && !scrollFlag) {
      if (propertyDetails?.length >= totalDetailsCount) return;
      setScrollFlag(true);
      getRegisterLogDetails({
        variables: {
          registerLogId,
          filter: {
            ...initialFilter,
            skip: propertyDetails?.length,
          },
        },
      });
    }
  };

  return (
    <div className="mb-20">
      <RouterPrompt
        openPrompt={state?.showPrompt}
        description={
          showTableFieldsPrompt?.current
            ? 'Some rows have missing required fields, and data for those rows will be lost. Are you sure you want to continue?'
            : 'Changes made will not be saved, are you sure?'
        }
        handleContinue={() => {
          navigate(
            `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}`,
          );
        }}
      />
      <CommonCard className="checklist-details mb-24" loading={isLoading}>
        <Row justify="space-between" align="middle">
          <Col span={isDesktopViewport && isEditControl ? 18 : 24}>
            <div
              className={`checklist-basic-details ${!isDesktopViewport && 'mb-10'}`}
            >
              {isEditControl && (
                <Form layout="vertical" form={form}>
                  <Form.Item
                    name="registerName"
                    rules={[
                      required,
                      characterWithoutWhiteSpace,
                      {
                        max: 250,
                        message: 'Name cannot be more than 250 characters',
                      },
                    ]}
                    label="Register Name"
                  >
                    <Input
                      placeholder="Enter Register Name"
                      allowClear
                      className="width-200"
                      disabled={isAuditor || isDisabled}
                      onBlur={async () => {
                        const value = form.getFieldValue('registerName');
                        if (registerData?.name === value) return;
                        if (registerId && value) {
                          await updateRegister({
                            variables: {
                              updateRegisterId: registerId,
                              data: {
                                name: value,
                              },
                            },
                          });
                        }
                      }}
                    />
                  </Form.Item>
                </Form>
              )}
            </div>
          </Col>
          {registerData?.status === REGISTER_STATUS_LABEL.DRAFT &&
          isDesktopViewport ? (
            <Col
              className="flex-vertical d-flex justify-end"
              span={isDesktopViewport ? 6 : null}
            >
              <div className="d-flex justify-end form-buttons">
                <CanPerform action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}>
                  <Button
                    shape="round"
                    icon={<DeleteButton width={32} height={32} />}
                    className={clsx(
                      !isDesktopViewport && 'width-percent-100 justify-center',
                      'delete-button-checklist delete-button d-flex',
                    )}
                    onClick={() => {
                      setShowDeleteRegisterModal(true);
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    shape="round"
                    type="primary"
                    icon={<SaveOutline />}
                    className={clsx(
                      !isDesktopViewport && 'width-percent-100 justify-center',
                      'edit-button-checklist d-flex',
                    )}
                    disabled={
                      !(
                        propertyDetails?.length > 0 &&
                        tableHeaders?.length > 0 &&
                        registerName
                      )
                    }
                    onClick={() => handleSave()}
                  >
                    Go Live
                  </Button>
                </CanPerform>
              </div>
            </Col>
          ) : null}
          {isEditControl && isDesktopViewport && (
            <Col
              className="flex-vertical d-flex justify-end"
              span={isDesktopViewport ? 6 : null}
            >
              <div className="d-flex justify-end">
                <div className="form-buttons width-percent-100">
                  <CanPerform action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}>
                    {registerData?.status === REGISTER_STATUS_LABEL.LIVE && (
                      <>
                        <Button
                          shape="round"
                          icon={<ArchiveOutline className="mr-5" />}
                          className={clsx(
                            'delete-button-checklist d-flex',
                            !isDesktopViewport &&
                              'width-percent-100 justify-center',
                          )}
                          onClick={() => handleRegisterArchive()}
                        >
                          Archive
                        </Button>
                        <Button
                          shape="round"
                          icon={<EditIcon />}
                          className={clsx(
                            'edit-button-checklist d-flex save-button',
                            !isDesktopViewport &&
                              'width-percent-100 justify-center',
                          )}
                          type="primary"
                          onClick={() => {
                            setConfirmationType(CONFIRMATION_TYPES.EDIT);
                            setShowModal(true);
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {registerData?.status ===
                      REGISTER_STATUS_LABEL.ARCHIVED && (
                      <Button
                        shape="round"
                        type="primary"
                        icon={<InboxOutlined />}
                        className={clsx(
                          !isDesktopViewport && 'justify-center fill-width',
                          'unarchive-button-checklist d-flex',
                        )}
                        onClick={() => {
                          setConfirmationType(CONFIRMATION_TYPES.UNARCHIVE);
                          setShowModal(true);
                        }}
                      >
                        Unarchive
                      </Button>
                    )}
                  </CanPerform>
                </div>
              </div>
            </Col>
          )}
          <Col span={24}>
            <div className="header d-flex align-center justify-between mb-10">
              <h3>Details</h3>
              <div className="register-header-buttons d-flex">
                <div id="register-details-btn" className="d-flex" />
                <div id="add-btn" />
              </div>
            </div>
            <Form
              form={form}
              layout="vertical"
              className="checklist-details-form d-flex"
            >
              <div
                className="d-flex flex-wrap max-height-200 overflow-y-auto"
                onScroll={handleScroll}
              >
                {!isEditControl && renderProperties(propertyDetails)}
              </div>
            </Form>
          </Col>
          <Col span={isDesktopViewport ? 12 : 24}>
            {isEditControl && renderPropertiesDetails()}
          </Col>
          {registerData?.status === REGISTER_STATUS_LABEL.DRAFT &&
          !isDesktopViewport ? (
            <Col
              className="flex-vertical d-flex justify-end mt-10"
              span={!isDesktopViewport && 24}
            >
              <div className="d-flex justify-end form-buttons">
                <CanPerform action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}>
                  <Button
                    shape="round"
                    icon={<DeleteButton width={32} height={32} />}
                    className={clsx(
                      !isDesktopViewport && 'width-percent-100 justify-center',
                      'delete-button-checklist delete-button d-flex',
                    )}
                    onClick={() => {
                      setShowDeleteRegisterModal(true);
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    shape="round"
                    type="primary"
                    icon={<SaveOutline />}
                    className={clsx(
                      !isDesktopViewport && 'width-percent-100 justify-center',
                      'edit-button-checklist d-flex',
                    )}
                    disabled={
                      !(
                        propertyDetails?.length > 0 &&
                        tableHeaders?.length > 0 &&
                        registerName
                      )
                    }
                    onClick={() => handleSave()}
                  >
                    Go Live
                  </Button>
                </CanPerform>
              </div>
            </Col>
          ) : null}
          {isEditControl && !isDesktopViewport && (
            <Col
              className="flex-vertical d-flex justify-end mt-10"
              span={!isDesktopViewport && 24}
            >
              <div className="d-flex justify-end">
                <div className="form-buttons width-percent-100">
                  <CanPerform action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}>
                    {registerData?.status === REGISTER_STATUS_LABEL.LIVE && (
                      <>
                        <Button
                          shape="round"
                          icon={<ArchiveOutline className="mr-5" />}
                          className={clsx(
                            'delete-button-checklist d-flex',
                            !isDesktopViewport &&
                              'width-percent-100 justify-center',
                          )}
                          onClick={() => handleRegisterArchive()}
                        >
                          Archive
                        </Button>
                        <Button
                          shape="round"
                          type="primary"
                          icon={<EditIcon />}
                          className={clsx(
                            'edit-button-checklist d-flex save-button',
                            !isDesktopViewport &&
                              'width-percent-100 justify-center',
                          )}
                          onClick={() => {
                            setConfirmationType(CONFIRMATION_TYPES.EDIT);
                            setShowModal(true);
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {registerData?.status ===
                      REGISTER_STATUS_LABEL.ARCHIVED && (
                      <Button
                        shape="round"
                        type="primary"
                        icon={<InboxOutlined />}
                        className={clsx(
                          !isDesktopViewport && 'justify-center fill-width',
                          'unarchive-button-checklist d-flex',
                        )}
                        onClick={() => {
                          setConfirmationType(CONFIRMATION_TYPES.UNARCHIVE);
                          setShowModal(true);
                        }}
                      >
                        Unarchive
                      </Button>
                    )}
                  </CanPerform>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </CommonCard>
      <AddEditRegisterFieldsTable
        tableHeaders={tableHeaders}
        setTableHeaders={setTableHeaders}
        tableColumns={tableColumns}
        setTableColumns={setTableColumns}
        registerDetailsData={registerDetailsData}
        isEditControl={isEditControl}
        isEditable={isEditable}
        isDisabled={isDisabled}
        isUserAssignedToProject={isUserAssignedToProject}
        showDetailPrompt={showDetailPrompt}
        showTableFieldsPrompt={showTableFieldsPrompt}
        handleTableRouterPromptUpdate={handleTableRouterPromptUpdate}
      />
      {showModal && (
        <ConfirmationModal
          type={confirmationType}
          showModal={showModal}
          setShowModal={setShowModal}
          handleRefetch={handleRegisterRefetch}
        />
      )}
      {showSaveModal && (
        <SaveRegisterLogModal
          handleRefetch={handleRegisterLogDetailRefetch}
          properties={properties}
          propertyDetails={propertyDetails}
          showModal={showSaveModal}
          setShowModal={setShowSaveModal}
          isEditControl={isEditControl}
          setIsEditControl={setIsEditControl}
          isEditableProperty={isEditableProperty}
          setIsEditableProperty={setIsEditableProperty}
        />
      )}
      {showDeleteRegisterModal && (
        <DeleteRegisterModal
          showModal={showDeleteRegisterModal}
          setShowModal={setShowDeleteRegisterModal}
          registerData={registerData}
        />
      )}
    </div>
  );
};

export default RegisterPropertyDetails;
